import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Database,
  FileCheck,
  Activity,
  Users,
  Receipt,
  ShieldCheck,
  Truck,
  ClipboardList,
  ArrowRight,
  CheckCircle2,
  Search,
  TrendingUp,
  Clock,
} from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from "recharts";
import { useLang } from "@/lib/i18n";
import { useSEO, siteUrl, breadcrumbJsonLd } from "@/lib/seo";
import { fetchFAQs } from "@/lib/api";
import FAQAccordion from "@/components/FAQAccordion";
import ContactForm from "@/components/ContactForm";

const trendData_nl = [
  { m: "Jul", v: 320 }, { m: "Aug", v: 360 }, { m: "Sep", v: 410 },
  { m: "Okt", v: 442 }, { m: "Nov", v: 468 }, { m: "Dec", v: 505 },
  { m: "Jan", v: 540 }, { m: "Feb", v: 588 },
];
const trendData_en = [
  { m: "Jul", v: 320 }, { m: "Aug", v: 360 }, { m: "Sep", v: 410 },
  { m: "Oct", v: 442 }, { m: "Nov", v: 468 }, { m: "Dec", v: 505 },
  { m: "Jan", v: 540 }, { m: "Feb", v: 588 },
];
const fleetSplit_nl = [
  { type: "Trekker", count: 42 },
  { type: "Trailer", count: 56 },
  { type: "Bestelbus", count: 24 },
  { type: "Koeltruck", count: 12 },
  { type: "Tank", count: 8 },
];
const fleetSplit_en = [
  { type: "Tractor", count: 42 },
  { type: "Trailer", count: 56 },
  { type: "Van", count: 24 },
  { type: "Reefer", count: 12 },
  { type: "Tank", count: 8 },
];

const FeatureRow = ({ icon: Icon, title, desc, dataTestid }) => (
  <div data-testid={dataTestid} className="flex gap-5 p-6 rounded-xl border border-white/10 bg-[#0F0F23] card-hover">
    <div className="w-11 h-11 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/20 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.8} />
    </div>
    <div>
      <h3 className="text-white text-base font-semibold mb-1.5">{title}</h3>
      <p className="text-white/55 text-sm leading-relaxed">{desc}</p>
    </div>
  </div>
);

const WaszBase = () => {
  const { t, lang } = useLang();
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    fetchFAQs("waszbase").then(setFaqs).catch(() => {});
  }, []);

  useSEO({
    title: lang === "nl"
      ? "WaszBase — Digitaal klantportaal voor wagenpark­administratie | Wasz.nl"
      : "WaszBase — Digital fleet administration portal | Wasz.nl",
    description: lang === "nl"
      ? "WaszBase: kentekenregistratie, certificaatbeheer, HACCP-dossiers, NVWA-export, sub-klanten en facturen. Het digitale brein achter elke Wasz.nl wasbeurt."
      : "WaszBase: license plate registration, certificate management, HACCP dossiers, NVWA export, sub-customers and invoices. The digital brain behind every Wasz.nl wash.",
    canonical: siteUrl("/waszbase"),
    image: "/images/project19.jpg",
    lang,
    jsonLd: [
      {
        id: "breadcrumb-jsonld",
        data: breadcrumbJsonLd([
          { name: lang === "nl" ? "Home" : "Home", url: siteUrl("/") },
          { name: "WaszBase", url: siteUrl("/waszbase") },
        ]),
      },
    ],
  });

  const features_nl = [
    { icon: Truck, title: "Kentekenregistratie", desc: "Registreer uw volledige vloot per kenteken. Voeg voertuigen toe via CSV-import of handmatig — gekoppeld aan locatie en sub-klant." },
    { icon: FileCheck, title: "Certificaatbeheer", desc: "Reinigingscertificaten worden automatisch gegenereerd en gekoppeld aan kenteken. Vervaldata zichtbaar, herinneringen automatisch." },
    { icon: ShieldCheck, title: "HACCP-conforme reiniging", desc: "Alle HACCP-conforme reinigingen krijgen een afzonderlijk certificaat per kenteken met operator, datum en gebruikt middel." },
    { icon: ClipboardList, title: "NVWA controles", desc: "Bij een controle exporteert u in 30 seconden het complete dossier per kenteken of per ladingstype als gestructureerde PDF." },
    { icon: Activity, title: "Wagenparkhistorie", desc: "Per voertuig ziet u elke wasbeurt: datum, locatie, type reiniging en operator-handtekening. Onbeperkte historie." },
    { icon: Users, title: "Sub-klanten", desc: "Voor logistieke verhuurders en hoofdaannemers: splits uw vloot in sub-klanten en bekijk per sub-klant welke voertuigen op welke wasdag zijn gereinigd." },
    { icon: ClipboardList, title: "Opdrachtenbeheer", desc: "Plan toekomstige wasbeurten of een structurele cyclus. Onze planners ontvangen uw verzoeken realtime, met locatie- en tijdsvenster." },
    { icon: Receipt, title: "Facturen & rapportage", desc: "Facturatie per wasdag op uw eigen ritme — wekelijks, tweewekelijks of maandelijks. In WaszBase ziet u per wasdag welke voertuigen en trekker-oplegger-combinaties zijn gewassen. Exporteer als PDF of CSV." },
  ];

  const features_en = [
    { icon: Truck, title: "License plate registry", desc: "Register your full fleet per license plate. Add vehicles via CSV import or manually — linked to location and sub-customer." },
    { icon: FileCheck, title: "Certificate management", desc: "Cleaning certificates are auto-generated and linked to license plate. Expiry dates visible, reminders automated." },
    { icon: ShieldCheck, title: "HACCP-compliant cleaning", desc: "Every HACCP-compliant cleaning gets its own certificate per license plate with operator, date and agent." },
    { icon: ClipboardList, title: "NVWA inspections", desc: "During an inspection you export the complete dossier per license plate or cargo type as a structured PDF in 30 seconds." },
    { icon: Activity, title: "Fleet history", desc: "Per vehicle you see every wash: date, location, cleaning type and operator signature. Unlimited history." },
    { icon: Users, title: "Sub-customers", desc: "For logistics rentals and main contractors: split your fleet into sub-customers and see per sub-customer which vehicles were cleaned on which wash-day." },
    { icon: ClipboardList, title: "Order management", desc: "Plan upcoming washes or structural cycles. Our planners receive your requests in real time, with location and time window." },
    { icon: Receipt, title: "Invoices & reporting", desc: "Invoicing per wash-day on your own rhythm — weekly, biweekly or monthly. In WaszBase you see per wash-day which vehicles and tractor-trailer combinations were cleaned. Export as PDF or CSV." },
  ];

  const features = lang === "nl" ? features_nl : features_en;
  const trendData = lang === "nl" ? trendData_nl : trendData_en;
  const fleetSplit = lang === "nl" ? fleetSplit_nl : fleetSplit_en;

  return (
    <main data-testid="waszbase-page" className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-20 pb-12 sm:pt-28 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10 cyber-grid opacity-40" />
        <div className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full bg-[#00D4FF]/8 blur-3xl -z-10" />

        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00D4FF]/25 bg-[#00D4FF]/5 mb-6">
                <Database className="w-3.5 h-3.5 text-[#00D4FF]" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#00D4FF] font-semibold">WaszBase Platform</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter text-white leading-[1.05]">
                {lang === "nl" ? (
                  <>Het <span className="gradient-text">digitale brein</span> achter elke wasbeurt.</>
                ) : (
                  <>The <span className="gradient-text">digital brain</span> behind every wash.</>
                )}
              </h1>
              <p className="mt-7 text-lg text-white/65 max-w-xl leading-relaxed">
                {lang === "nl"
                  ? "Eén klantportaal voor uw volledige wagenparkadministratie: kentekens, certificaten, NVWA-dossiers, facturen en opdrachten — realtime."
                  : "One customer portal for your full fleet administration: license plates, certificates, NVWA dossiers, invoices and orders — in real time."}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href="https://www.waszbase.co.nl"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="waszbase-demo-cta"
                >
                  <button className="btn-primary">
                    {t("hero.cta_demo")} <ArrowRight className="w-4 h-4" />
                  </button>
                </a>
                <Link to="/contact?type=quote" data-testid="waszbase-quote-cta">
                  <button className="btn-secondary">{t("common.request_quote")}</button>
                </Link>
              </div>
            </div>

            {/* Dashboard mock */}
            <div className="relative">
              <div className="rounded-2xl border border-white/10 bg-[#0F0F23] overflow-hidden shadow-[0_40px_100px_-30px_rgba(0,212,255,0.25)]">
                <div className="px-5 py-3 border-b border-white/10 bg-[#050509] flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]/70"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#facc15]/70"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]/70"></span>
                  <div className="ml-3 flex-1 flex items-center gap-2">
                    <Search className="w-3 h-3 text-white/30" />
                    <span className="text-[11px] mono text-white/40">waszbase.co.nl/dashboard</span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {(lang === "nl"
                      ? [
                          { l: "Vloot", v: "142", trend: "+4" },
                          { l: "Wasbeurten", v: "588", trend: "+18%" },
                          { l: "Cert. actief", v: "139", trend: "100%" },
                        ]
                      : [
                          { l: "Fleet", v: "142", trend: "+4" },
                          { l: "Washes", v: "588", trend: "+18%" },
                          { l: "Cert. active", v: "139", trend: "100%" },
                        ]
                    ).map((s, i) => (
                      <div key={i} className="rounded-lg border border-white/10 bg-[#050509] p-3">
                        <div className="text-[10px] uppercase tracking-wider text-white/40">{s.l}</div>
                        <div className="text-xl font-bold text-white mt-1">{s.v}</div>
                        <div className="text-[10px] mono text-[#10B981] mt-1">{s.trend}</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-lg border border-white/10 bg-[#050509] p-4 mb-4">
                    <div className="flex items-baseline justify-between mb-3">
                      <div className="text-[11px] uppercase tracking-wider text-white/40">{lang === "nl" ? "Wasbeurten / maand" : "Washes / month"}</div>
                      <div className="text-xs mono text-[#00D4FF] inline-flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> +18%
                      </div>
                    </div>
                    <div className="h-32" style={{ minHeight: 128 }}>
                      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                        <AreaChart data={trendData} margin={{ top: 4, right: 0, left: -28, bottom: 0 }}>
                          <defs>
                            <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#00D4FF" stopOpacity={0.5} />
                              <stop offset="100%" stopColor="#00D4FF" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area type="monotone" dataKey="v" stroke="#00D4FF" strokeWidth={2} fill="url(#grad)" />
                          <XAxis dataKey="m" stroke="rgba(255,255,255,0.25)" tick={{ fontSize: 9 }} />
                          <YAxis stroke="rgba(255,255,255,0.25)" tick={{ fontSize: 9 }} />
                          <Tooltip
                            contentStyle={{ background: "#0F0F23", border: "1px solid rgba(255,255,255,0.1)", fontSize: 11 }}
                            cursor={{ stroke: "rgba(0,212,255,0.3)" }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="rounded-lg border border-white/10 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-[#050509] text-white/40 text-[10px] uppercase tracking-wider">
                        <tr>
                          <th className="text-left p-2.5">{lang === "nl" ? "Kenteken" : "License plate"}</th>
                          <th className="text-left p-2.5">{lang === "nl" ? "Type" : "Type"}</th>
                          <th className="text-left p-2.5">HACCP</th>
                        </tr>
                      </thead>
                      <tbody className="text-white/70">
                        {(lang === "nl"
                          ? [
                              ["BX-04-NL", "Trekker", "n.v.t."],
                              ["VR-89-DH", "Trailer", "OK"],
                              ["TR-12-AM", "Koel", "OK"],
                            ]
                          : [
                              ["BX-04-NL", "Tractor", "N/A"],
                              ["VR-89-DH", "Trailer", "OK"],
                              ["TR-12-AM", "Reefer", "OK"],
                            ]
                        ).map((r, i) => (
                          <tr key={i} className="border-t border-white/5">
                            <td className="p-2.5 mono text-[#00D4FF]">{r[0]}</td>
                            <td className="p-2.5">{r[1]}</td>
                            <td className="p-2.5">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                r[2] === "OK"
                                  ? "bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30"
                                  : "bg-white/5 text-white/40 border border-white/10"
                              }`}>{r[2]}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="section-overline mb-3">{lang === "nl" ? "Functionaliteit" : "Functionality"}</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {lang === "nl" ? "Acht modules. Eén dossier per kenteken." : "Eight modules. One dossier per license plate."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {features.map((f, i) => (
              <FeatureRow key={i} dataTestid={`waszbase-feature-${i}`} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* Charts row */}
      <section className="py-20 bg-[#0a0a18] border-y border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#0F0F23] p-7">
              <div className="flex items-baseline justify-between mb-5">
                <div>
                  <p className="section-overline mb-2">Trend</p>
                  <h3 className="text-xl font-bold text-white">{lang === "nl" ? "Wasbeurten per maand" : "Washes per month"}</h3>
                </div>
                <div className="text-xs mono text-[#10B981]">{lang === "nl" ? "+18% vs vorig jaar" : "+18% YoY"}</div>
              </div>
              <div className="h-56" style={{ minHeight: 224 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                  <LineChart data={trendData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                    <Line type="monotone" dataKey="v" stroke="#00D4FF" strokeWidth={2.5} dot={{ fill: "#00D4FF" }} />
                    <XAxis dataKey="m" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11 }} />
                    <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: "#050509", border: "1px solid rgba(255,255,255,0.1)" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0F0F23] p-7">
              <div className="flex items-baseline justify-between mb-5">
                <div>
                  <p className="section-overline mb-2">{lang === "nl" ? "Vloot­samenstelling" : "Fleet composition"}</p>
                  <h3 className="text-xl font-bold text-white">{lang === "nl" ? "Voertuigtypes in beheer" : "Vehicle types under management"}</h3>
                </div>
                <div className="text-xs mono text-white/50">{lang === "nl" ? "142 totaal" : "142 total"}</div>
              </div>
              <div className="h-56" style={{ minHeight: 224 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                  <BarChart data={fleetSplit} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                    <Bar dataKey="count" fill="#00D4FF" radius={[6, 6, 0, 0]} />
                    <XAxis dataKey="type" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11 }} />
                    <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: "#050509", border: "1px solid rgba(255,255,255,0.1)" }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules deep-dive */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="section-overline mb-3">{lang === "nl" ? "Modules in detail" : "Modules in detail"}</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {lang === "nl" ? "Wat WaszBase concreet voor uw wagenpark doet." : "What WaszBase concretely does for your fleet."}
            </h2>
          </div>

          <div className="space-y-12">
            {(lang === "nl" ? [
              {
                tag: "Kentekenregistratie",
                title: "Eén dossier per voertuig, geen losse e-mails",
                body: "Importeer uw volledige vloot via CSV of Excel — kentekens, locaties, sub-klanten en kostenplaatsen. Per kenteken legt WaszBase automatisch vast: alle wasbeurten, certificaten en operator-handtekeningen. Voor verhuurders en hoofdaannemers splitsen we per sub-klant zonder dat u losse contracten nodig heeft.",
                bullets: [
                  "CSV/Excel import voor 1000+ kentekens in één keer",
                  "Sub-klant en kostenplaats-structuur",
                  "Onbeperkte historie per kenteken",
                  "Eenvoudige CSV/Excel uitwisseling met uw eigen systemen",
                ],
              },
              {
                tag: "HACCP-registratiebeheer",
                title: "Documentatie klaar om te tonen, zonder paperwork",
                body: "Elke reiniging die volgens HACCP-werkwijze wordt uitgevoerd genereert automatisch een digitale registratie met operator-handtekening, gebruikt middel, dosering en datum. Vervaldata zijn zichtbaar, herinneringen worden automatisch verstuurd. Zo bent u nooit verrast door een controle of een verlopen registratie.",
                bullets: [
                  "Uniek registratienummer per wasbeurt",
                  "Automatische koppeling aan kenteken",
                  "Optionele dosering- en middelvermelding",
                  "Onbeperkte bewaartermijn",
                ],
              },
              {
                tag: "NVWA-export",
                title: "Volledige PDF in 30 seconden",
                body: "Bij een NVWA-controle opent u in WaszBase het kenteken-dossier of selecteert u een ladingstype. In 30 seconden draait u een gestructureerd PDF-pakket met alle wasbeurten, certificaten en operator-handtekeningen van de afgelopen 24 maanden. Inspecteur tevreden, u terug aan het werk.",
                bullets: [
                  "Per kenteken óf per ladingstype",
                  "Standaard tijdsbereik 24 maanden, schaalbaar",
                  "Inclusief gebruikt middel en dosering",
                  "Direct deelbaar met externe auditors",
                ],
              },
              {
                tag: "Wagenparkhistorie",
                title: "Realtime overzicht van uw vlootactiviteit",
                body: "Per voertuig ziet u elke wasbeurt: datum, locatie, type reiniging, operator en handtekening. Op vlootniveau krijgt u trends: leverbetrouwbaarheid, gemiddelde doorzettijd, water- en middelenverbruik. Onze KPI-dashboards helpen u onderbouwd beslissen over cyclus-aanpassingen of vlootuitbreidingen.",
                bullets: [
                  "Realtime activity feed",
                  "Trend-analyse per voertuigtype",
                  "Vergelijking tussen locaties",
                  "Maandelijkse KPI-rapportage als export",
                ],
              },
              {
                tag: "Factuur- en kostenbeheer",
                title: "Facturatie per wasdag, op uw eigen ritme",
                body: "Facturatie loopt per wasdag. Eén factuurregel per uitvoeringsdatum, met daaraan gekoppeld een specificatie van alle gewassen voertuigen — inclusief trekker-oplegger-combinaties. U kiest zelf of u facturen wekelijks, tweewekelijks of maandelijks wilt ontvangen. Exporteer naar PDF voor uw archief of CSV voor import in uw eigen boekhoudpakket. Opgemaakt volgens de Nederlandse btw-richtlijnen.",
                bullets: [
                  "Factuurregel per wasdag, met voertuigspecificatie",
                  "Trekker-oplegger-combinaties zichtbaar per dag",
                  "Frequentie naar keuze: wekelijks, tweewekelijks of maandelijks",
                  "PDF en CSV export, compatibel met gangbare boekhoudpakketten",
                ],
              },
              {
                tag: "Opdrachten & planning",
                title: "Geen e-mails meer, alleen één planning",
                body: "Plan toekomstige wasbeurten of een structurele cyclus direct in WaszBase. Onze planners ontvangen uw verzoeken realtime, met locatie, tijdsvenster en bijzonderheden. Voor structurele klanten draait de cyclus volledig automatisch — u hoeft alleen aan-/afmeldingen door te geven bij wijzigingen.",
                bullets: [
                  "Cyclus volledig automatisch",
                  "Ad-hoc opdrachten binnen 24 uur ingepland",
                  "SLA-bewaking en escalatie ingebouwd",
                  "Operationele evaluatie per maand of kwartaal",
                ],
              },
            ] : [
              {
                tag: "License plate registry",
                title: "One dossier per vehicle, no scattered emails",
                body: "Import your full fleet via CSV or Excel — license plates, locations, sub-customers and cost centres. For each plate WaszBase automatically logs: every wash, certificate and operator signature. For rentals and main contractors we split per sub-customer without needing separate contracts.",
                bullets: [
                  "CSV/Excel import for 1,000+ plates at once",
                  "Sub-customer and cost-centre structure",
                  "Unlimited history per plate",
                  "Easy CSV/Excel exchange with your own systems",
                ],
              },
              {
                tag: "HACCP certificate management",
                title: "Documentation ready to present, without paperwork",
                body: "Every cleaning performed to HACCP standards auto-generates a digital certificate with operator signature, agent used, dosage and date. Expiry dates are visible, reminders automated. You'll never be caught off-guard by an inspection or an expired certificate.",
                bullets: [
                  "Unique certificate ID per wash",
                  "Automatic linking to license plate",
                  "Optional agent and dosage notes",
                  "Unlimited retention",
                ],
              },
              {
                tag: "NVWA export",
                title: "Complete PDF in 30 seconds",
                body: "During an NVWA inspection you open the plate dossier or select a cargo type in WaszBase. Within 30 seconds you produce a structured PDF bundle with all washes, certificates and operator signatures of the past 24 months. Inspector satisfied, you back to work.",
                bullets: [
                  "Per license plate or per cargo type",
                  "Default 24 months range, scalable",
                  "Includes agent and dosage",
                  "Directly shareable with external auditors",
                ],
              },
              {
                tag: "Fleet history",
                title: "Real-time view of your fleet activity",
                body: "Per vehicle you see every wash: date, location, cleaning type, operator and signature. At fleet level you get trends: cycle adherence, average turnaround, water and agent usage. Our KPI dashboards help you decide on cycle adjustments or fleet growth with evidence.",
                bullets: [
                  "Real-time activity feed",
                  "Trend analysis per vehicle type",
                  "Site-to-site comparison",
                  "Monthly KPI review export",
                ],
              },
              {
                tag: "Invoicing & cost control",
                title: "Invoicing per wash-day, on your rhythm",
                body: "Invoicing runs per wash-day. One invoice line per execution date, with a specification of all vehicles cleaned that day — including tractor-trailer combinations. You decide whether to receive invoices weekly, biweekly or monthly. Export to PDF for your records or CSV for import into your accounting package. Prepared in line with Dutch VAT guidelines.",
                bullets: [
                  "Invoice line per wash-day, with vehicle specification",
                  "Tractor-trailer combinations visible per day",
                  "Frequency of choice: weekly, biweekly or monthly",
                  "PDF and CSV export, compatible with common accounting packages",
                ],
              },
              {
                tag: "Orders & scheduling",
                title: "No more emails, just one schedule",
                body: "Schedule future washes or structural cycles directly in WaszBase. Our planners receive your requests in real time, with location, time window and remarks. For structural customers the cycle runs fully automatic — you only confirm exceptions when something changes.",
                bullets: [
                  "Cycle runs fully automatic",
                  "Ad-hoc orders scheduled within 24 hours",
                  "Built-in SLA tracking and escalation",
                  "Monthly or quarterly operational review",
                ],
              },
            ]).map((m, i) => (
              <div key={i} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start rounded-2xl border border-white/10 bg-[#0F0F23] p-7 sm:p-10">
                <div className="lg:col-span-7">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#00D4FF] font-semibold mb-3">{m.tag}</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-4">{m.title}</h3>
                  <p className="text-white/65 text-[16px] leading-relaxed">{m.body}</p>
                </div>
                <ul className="lg:col-span-5 space-y-2.5 lg:border-l lg:border-white/10 lg:pl-8">
                  {m.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-white/75 text-[14px]">
                      <CheckCircle2 className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="section-overline mb-3">{lang === "nl" ? "Onboarding" : "Onboarding"}</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              {lang === "nl" ? "Live in 7 werkdagen." : "Live in 7 business days."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              { n: "01", t_nl: "Intake & vlootmapping", d_nl: "We brengen samen uw vloot, locaties en sub-klanten in kaart.", t_en: "Intake & fleet mapping", d_en: "Together we map your fleet, locations and sub-customers." },
              { n: "02", t_nl: "WaszBase configuratie", d_nl: "Kentekenregister, gebruikersrollen en facturatiestructuur opgezet.", t_en: "WaszBase configuration", d_en: "License plate registry, user roles and billing structure set up." },
              { n: "03", t_nl: "Eerste wascyclus", d_nl: "We voeren een proefcyclus uit en finetunen het protocol.", t_en: "First wash cycle", d_en: "We run a trial cycle and fine-tune the protocol." },
              { n: "04", t_nl: "Live & maandelijkse evaluatie", d_nl: "Volledige overdracht. KPI-evaluatie elke maand met uw wagenpark beheerder.", t_en: "Live & monthly review", d_en: "Full handover. KPI review every month with your fleet manager." },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-[#0F0F23] p-6 card-hover">
                <div className="text-[#00D4FF] mono text-3xl font-bold mb-3">{s.n}</div>
                <h3 className="text-white text-lg font-semibold mb-2">{lang === "nl" ? s.t_nl : s.t_en}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{lang === "nl" ? s.d_nl : s.d_en}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-24 bg-[#0a0a18] border-y border-white/5">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                {lang === "nl" ? "WaszBase — veelgestelde vragen" : "WaszBase — frequently asked"}
              </h2>
            </div>
            <FAQAccordion items={faqs} dataTestid="waszbase-faq" />
          </div>
        </section>
      )}

      {/* Demo CTA */}
      <section className="py-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              {lang === "nl" ? "Plan een gratis demo" : "Schedule a free demo"}
            </h2>
            <p className="text-white/55 mt-4 max-w-md mx-auto">
              {lang === "nl"
                ? "30 minuten, geen verplichtingen. We laten zien hoe WaszBase aansluit op úw operationele werkwijze."
                : "30 minutes, no obligations. We show how WaszBase fits your operational way of working."}
            </p>
          </div>
          <ContactForm
            defaultType="demo"
            dataTestid="waszbase-demo-form"
            hideTypeSelector
          />
        </div>
      </section>
    </main>
  );
};

export default WaszBase;
